/**
 * Runtime for operator-authored `FieldValidator` rules.
 *
 * Mirrors `@upg/core/types::FieldValidator`. The browser side is a thin
 * pure-JS evaluator — keeping zod off the checkout bundle. The server PUT
 * handler uses zod to validate the rule SHAPE (so misconfigured operator
 * input never reaches DDB), but at runtime in the SPA we just enforce the
 * declarative semantics.
 *
 * Returns `{ ok, value }` on success (value is post-transform — the form
 * submits the normalised string) or `{ ok: false, error }` on failure.
 */
import type { FieldValidator } from './api.js';

export interface FieldValidatorRuntime {
  run(raw: string): { ok: true; value: string } | { ok: false; value: string; error: string };
}

interface PresetRule {
  pattern: RegExp;
  message: string;
  /** When true, min/max apply to the numeric value, not string length. */
  numeric?: boolean;
}

const PRESETS: Record<string, PresetRule> = {
  free_text: { pattern: /^.*$/, message: '' },
  // Pragmatic email regex — good enough for a checkout, not RFC-perfect.
  email: {
    pattern: /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
    message: 'Enter a valid email address.',
  },
  // Phone: digits / + / spaces / parens / dashes; 7–20 long.
  phone: {
    pattern: /^[+()\-\s\d]{7,20}$/,
    message: 'Enter a valid phone number.',
  },
  numeric: {
    pattern: /^-?\d+(\.\d+)?$/,
    message: 'Enter a number.',
    numeric: true,
  },
  alphanumeric: {
    pattern: /^[A-Za-z0-9]+$/,
    message: 'Letters and numbers only — no spaces or punctuation.',
  },
  iban: {
    pattern: /^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/,
    message: 'Enter a valid IBAN.',
  },
  uppercase_alpha: {
    pattern: /^[A-Z]+$/,
    message: 'Uppercase letters only.',
  },
};

export function buildFieldValidator(rule: FieldValidator): FieldValidatorRuntime {
  const preset = rule.format ? PRESETS[rule.format] : undefined;
  // Custom regex — compiled once. Invalid sources must not throw at runtime;
  // we silently fall back to "no extra constraint" so a misconfigured rule
  // never bricks the checkout (operators see the failure in the admin
  // builder, not on a shopper's form).
  let customRegex: RegExp | null = null;
  if (rule.pattern) {
    try {
      customRegex = new RegExp(rule.pattern);
    } catch {
      customRegex = null;
    }
  }

  return {
    run(raw: string) {
      const transformed = applyTransform(raw, rule.transform);

      if (!transformed) {
        if (rule.required) {
          return {
            ok: false,
            value: transformed,
            error: rule.customMessage ?? 'This field is required.',
          };
        }
        return { ok: true, value: transformed };
      }

      // Preset (format) match.
      if (preset && !preset.pattern.test(transformed)) {
        return {
          ok: false,
          value: transformed,
          error: rule.customMessage ?? preset.message,
        };
      }

      // Min/max — numeric range for numeric presets, string length otherwise.
      if (preset?.numeric) {
        const n = Number(transformed);
        if (rule.min !== undefined && n < rule.min) {
          return {
            ok: false,
            value: transformed,
            error: rule.customMessage ?? `Must be at least ${rule.min}.`,
          };
        }
        if (rule.max !== undefined && n > rule.max) {
          return {
            ok: false,
            value: transformed,
            error: rule.customMessage ?? `Must be at most ${rule.max}.`,
          };
        }
      } else {
        if (rule.min !== undefined && transformed.length < rule.min) {
          return {
            ok: false,
            value: transformed,
            error: rule.customMessage ?? `Must be at least ${rule.min} characters.`,
          };
        }
        if (rule.max !== undefined && transformed.length > rule.max) {
          return {
            ok: false,
            value: transformed,
            error: rule.customMessage ?? `Must be at most ${rule.max} characters.`,
          };
        }
      }

      // Operator-authored regex always runs after preset + range.
      if (customRegex && !customRegex.test(transformed)) {
        return {
          ok: false,
          value: transformed,
          error:
            rule.patternMessage ??
            rule.customMessage ??
            'Value doesn’t match the required format.',
        };
      }

      return { ok: true, value: transformed };
    },
  };
}

function applyTransform(raw: string, t: FieldValidator['transform']): string {
  switch (t) {
    case 'trim':
      return raw.trim();
    case 'uppercase':
      return raw.trim().toUpperCase();
    case 'lowercase':
      return raw.trim().toLowerCase();
    default:
      return raw;
  }
}
